'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useCurrentUser, hasPortalAccess } from '@paeffgen/react/crm-documents/feature-auth';
import { AppShell } from '../components/layout/app-shell';
import { AuthShell } from '../components/layout/auth-shell';
import { PublicInfoShell } from '../components/layout/public-info-shell';

/** Ausgeloggte Auth-Routen — bekommen die schlanke AuthShell statt der Sidebar. */
const isAuthRoute = (pathname: string) =>
  pathname === '/login' || pathname.startsWith('/register');

/** Öffentliche Hilfe/FAQ — auch ohne Login erreichbar. */
const isInfoRoute = (pathname: string) =>
  pathname === '/informationen' || pathname.startsWith('/informationen/');

/**
 * Client-side layout wrapper.
 * - Auth-Seiten (/login, /register, /register/confirm) → AuthShell (ohne Sidebar).
 * - Hilfe/FAQ (/informationen) ohne vollen Portal-Zugang → schlanke PublicInfoShell.
 * - Alle übrigen (authentifizierten) Seiten → AppShell mit Sidebar.
 * Separated from layout.tsx (Server Component) to keep the shells client-side.
 */
export function ClientLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { user, isAuthenticated } = useCurrentUser();

  if (isAuthRoute(pathname)) {
    return <AuthShell>{children}</AuthShell>;
  }

  // Öffentliche FAQ: nur voll freigeschaltete Nutzer bekommen die AppShell mit
  // Sidebar/Warenkorb (setzt Login voraus); anonyme oder noch nicht freigeschaltete
  // Nutzer bekommen die schlanke öffentliche Hülle.
  if (isInfoRoute(pathname) && !(isAuthenticated && hasPortalAccess(user))) {
    return <PublicInfoShell>{children}</PublicInfoShell>;
  }

  return <AppShell>{children}</AppShell>;
}
